import type { QuizAnswer, QuizAttempt, QuizQuestion } from './models';

export interface QuizEvaluation {
  answers: QuizAnswer[];
  correctCount: number;
  totalQuestions: number;
  score: number;
  xpEarned: number;
}

export function evaluateQuiz(questions: QuizQuestion[], selected: Record<string, string>): QuizEvaluation {
  const active = questions.filter((question) => question.active).sort((a, b) => a.order - b.order);
  const answers = active.map((question) => {
    const choiceId = selected[question.id] ?? '';
    return { questionId: question.id, choiceId, correct: choiceId === question.correctChoiceId };
  });
  const correctCount = answers.filter((answer) => answer.correct).length;
  const totalQuestions = active.length;
  const score = totalQuestions ? Math.round((correctCount / totalQuestions) * 100) : 0;
  const completionXp = totalQuestions ? 20 : 0;
  const accuracyXp = correctCount * 10;
  const perfectBonus = totalQuestions > 0 && correctCount === totalQuestions ? 20 : 0;
  return { answers, correctCount, totalQuestions, score, xpEarned: completionXp + accuracyXp + perfectBonus };
}

export function createQuizAttempt(params: {
  id: string;
  childId: string;
  articleId: string;
  questions: QuizQuestion[];
  selected: Record<string, string>;
  completedAt?: string;
}): QuizAttempt {
  const evaluation = evaluateQuiz(params.questions, params.selected);
  return {
    id: params.id,
    childId: params.childId,
    articleId: params.articleId,
    ...evaluation,
    completedAt: params.completedAt ?? new Date().toISOString(),
    reviewedWrongAnswers: evaluation.correctCount === evaluation.totalQuestions
  };
}

export function latestAttemptFor(attempts: QuizAttempt[], articleId: string): QuizAttempt | undefined {
  return attempts
    .filter((attempt) => attempt.articleId === articleId)
    .sort((a, b) => String(b.completedAt).localeCompare(String(a.completedAt)))[0];
}

export function difficultConceptIds(questions: QuizQuestion[], attempts: QuizAttempt[]): string[] {
  const questionMap = new Map(questions.map((question) => [question.id, question]));
  const misses = new Map<string, number>();
  for (const attempt of attempts) {
    for (const answer of attempt.answers) {
      if (answer.correct) continue;
      const conceptId = questionMap.get(answer.questionId)?.conceptId;
      if (conceptId) misses.set(conceptId, (misses.get(conceptId) ?? 0) + 1);
    }
  }
  return [...misses.entries()].sort((a, b) => b[1] - a[1]).map(([conceptId]) => conceptId);
}
